import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from 'src/config/firebase';
import ApiConfig from 'src/config/api-config';

export type PackagePresetTier = 'Package 1' | 'Package 2' | 'Package 3';

export interface TrainerPackage {
  id: string;
  trainerId: string;
  templateId?: string;
  name: string;
  presetTier: PackagePresetTier;
  shortDescription: string;
  whoIsThisFor: string;
  price: number;
  currency: string;
  currencySymbol: string;
  duration: string;
  coaching: {
    inquiriesPerWeek: number;
    checkInsPerWeek: number;
  };
  optionalExtras: {
    nutritionalPlan: boolean;
    nutritionalGuidance: boolean;
  };
  delivery: {
    initialProgramReadyWithin: string;
    nutritionalPlanDelivery?: string;
  };
  terms: {
    coachingPeriod: string;
    renewal: string;
  };
  status: 'published' | 'draft';
  createdAt: string;
  updatedAt: string;
}

export const VALID_PRESET_TIERS: PackagePresetTier[] = ['Package 1', 'Package 2', 'Package 3'];

export const VALID_READY_WITHIN_OPTIONS = [
  'Within 24 hours',
  'Within 48 hours',
  'Within 3 days',
  'Within 5 days',
  'Within 7 days',
];

export const VALID_NUTRITIONAL_DELIVERY_OPTIONS = [
  'Within 24 hours',
  'Within 48 hours',
  'Within 3 days',
  'Within 5 days',
];

export const VALID_CURRENCIES = ['GBP', 'USD', 'EUR'] as const;
export type ValidCurrency = (typeof VALID_CURRENCIES)[number];

export const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
};

export const VALID_DURATIONS = [
  '1 week',
  '2 weeks',
  '3 weeks',
  '4 weeks/ 1 month',
  '6 weeks',
  '8 weeks/ 2 months',
  '12 weeks/ 3 months',
];

export interface PackagePresetTemplate {
  id: string;
  tier: PackagePresetTier;
  templateId: string;
  label: string;
}

export const PACKAGE_PRESET_TEMPLATES: PackagePresetTemplate[] = [
  {
    id: 'pkg_1',
    tier: 'Package 1',
    templateId: 'pkg_template_1',
    label: 'Package 1',
  },
  {
    id: 'pkg_2',
    tier: 'Package 2',
    templateId: 'pkg_template_2',
    label: 'Package 2',
  },
  {
    id: 'pkg_3',
    tier: 'Package 3',
    templateId: 'pkg_template_3',
    label: 'Package 3',
  },
];

export const DEFAULT_PACKAGE_TEMPLATE = PACKAGE_PRESET_TEMPLATES[0];

export const getPackageTemplateConfig = (tier?: string): PackagePresetTemplate => {
  return (
    PACKAGE_PRESET_TEMPLATES.find((t) => t.tier.toLowerCase() === (tier || '').toLowerCase()) ||
    DEFAULT_PACKAGE_TEMPLATE
  );
};

export const DEFAULT_PACKAGE_PRESETS: Record<
  PackagePresetTier,
  Omit<TrainerPackage, 'id' | 'trainerId' | 'createdAt' | 'updatedAt' | 'status'>
> = {
  'Package 1': {
    templateId: PACKAGE_PRESET_TEMPLATES[0].templateId,
    name: '',
    presetTier: PACKAGE_PRESET_TEMPLATES[0].tier,
    shortDescription: '',
    whoIsThisFor: '',
    price: 0,
    currency: 'GBP',
    currencySymbol: '£',
    duration: '4 weeks/ 1 month',
    coaching: {
      inquiriesPerWeek: 1,
      checkInsPerWeek: 1,
    },
    optionalExtras: {
      nutritionalPlan: false,
      nutritionalGuidance: false,
    },
    delivery: {
      initialProgramReadyWithin: 'Within 3 days',
      nutritionalPlanDelivery: 'Within 3 days',
    },
    terms: {
      coachingPeriod: '4-week coaching period',
      renewal: 'No automatic renewal',
    },
  },
  'Package 2': {
    templateId: PACKAGE_PRESET_TEMPLATES[1].templateId,
    name: '',
    presetTier: PACKAGE_PRESET_TEMPLATES[1].tier,
    shortDescription: '',
    whoIsThisFor: '',
    price: 0,
    currency: 'GBP',
    currencySymbol: '£',
    duration: '4 weeks/ 1 month',
    coaching: {
      inquiriesPerWeek: 1,
      checkInsPerWeek: 1,
    },
    optionalExtras: {
      nutritionalPlan: false,
      nutritionalGuidance: false,
    },
    delivery: {
      initialProgramReadyWithin: 'Within 3 days',
      nutritionalPlanDelivery: 'Within 3 days',
    },
    terms: {
      coachingPeriod: '4-week coaching period',
      renewal: 'No automatic renewal',
    },
  },
  'Package 3': {
    templateId: PACKAGE_PRESET_TEMPLATES[2].templateId,
    name: '',
    presetTier: PACKAGE_PRESET_TEMPLATES[2].tier,
    shortDescription: '',
    whoIsThisFor: '',
    price: 0,
    currency: 'GBP',
    currencySymbol: '£',
    duration: '4 weeks/ 1 month',
    coaching: {
      inquiriesPerWeek: 1,
      checkInsPerWeek: 1,
    },
    optionalExtras: {
      nutritionalPlan: false,
      nutritionalGuidance: false,
    },
    delivery: {
      initialProgramReadyWithin: 'Within 3 days',
      nutritionalPlanDelivery: 'Within 3 days',
    },
    terms: {
      coachingPeriod: '4-week coaching period',
      renewal: 'No automatic renewal',
    },
  },
};

/**
 * Standardized API Error Code to Friendly Message Mapping (per AGENTS.md)
 */
export const PACKAGE_ERROR_CODES: Record<string, string> = {
  ERR_PACKAGE_NAME_REQUIRED: 'Package Name is required.',
  ERR_PACKAGE_NAME_TOO_SHORT: 'Package Name must be at least 2 characters.',
  ERR_PACKAGE_NAME_TOO_LONG: 'Package Name cannot exceed 50 characters.',
  ERR_PACKAGE_NAME_INVALID: 'Package Name must contain at least one alphabetical letter.',
  ERR_SHORT_DESC_REQUIRED: 'Short Description is required.',
  ERR_SHORT_DESC_TOO_SHORT: 'Short Description must be at least 5 characters.',
  ERR_SHORT_DESC_TOO_LONG: 'Short Description cannot exceed 200 characters.',
  ERR_SHORT_DESC_INVALID: 'Short Description must contain at least one alphabetical letter.',
  ERR_WHO_IS_THIS_FOR_REQUIRED: 'Please specify who this package is for.',
  ERR_WHO_IS_THIS_FOR_TOO_SHORT: 'Who this is for must be at least 5 characters.',
  ERR_WHO_IS_THIS_FOR_TOO_LONG: 'Who this is for cannot exceed 200 characters.',
  ERR_WHO_IS_THIS_FOR_INVALID: 'Who this is for must contain at least one alphabetical letter.',
  ERR_PRICE_REQUIRED: 'Price is required.',
  ERR_INVALID_PRICE: 'Please enter a valid price.',
  ERR_INVALID_PRICE_DECIMALS: 'Price cannot have more than 2 decimal places.',
  ERR_PRICE_OUT_OF_RANGE: 'Price must be between £1 and £99,999.',
  ERR_INVALID_PRESET_TIER: 'Invalid package preset tier selected.',
  ERR_INVALID_INQUIRIES_COUNT: 'Inquiries per week must be between 0 and 10.',
  ERR_INVALID_CHECKINS_COUNT: 'Check-ins per week must be between 0 and 10.',
  ERR_INVALID_DELIVERY_TIME: 'Please select a valid delivery timeline.',
  ERR_NUTRITIONAL_DELIVERY_REQUIRED: 'Nutritional plan delivery timeline is required.',
  ERR_MAX_PACKAGES_REACHED: 'You can create a maximum of 3 packages.',
  ERR_PACKAGE_NOT_FOUND: 'The specified package was not found.',
  ERR_MISSING_UID: 'Trainer session expired. Please log in again.',
  ERR_INVALID_UID: 'Invalid user identifier.',
  ERR_INTERNAL_SERVER_ERROR: 'An error occurred while communicating with the server.',
};

export interface ReadyWithinValidationResult {
  isValid: boolean;
  error?: string;
  days?: number;
}

export type TemplateType = 'checkin_policy' | 'contractual_terms' | 'cancellation_policy';

export interface ClauseItem {
  number: number;
  title: string;
  content: string;
}

export interface DocumentTemplateConfig {
  title: string;
  subtitle: string;
  clauses: ClauseItem[];
  lastUpdated: string;
  version: string;
}

export const DEFAULT_DOCUMENT_TEMPLATES: Record<TemplateType, DocumentTemplateConfig> = {
  contractual_terms: {
    title: 'Contractual terms',
    subtitle:
      'Please review the terms that apply to your use of the platform and paid subscription',
    lastUpdated: 'May 1, 2026',
    version: '2.1',
    clauses: [
      {
        number: 1,
        title: 'Acceptance of Terms',
        content:
          'By accessing or using miniPT ("the Platform"), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree, you may not use the Platform.',
      },
      {
        number: 2,
        title: 'Account Eligibility',
        content:
          'By accessing or using miniPT ("the Platform"), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree, you may not use the Platform.',
      },
      {
        number: 3,
        title: 'Subscription & Billing',
        content:
          'miniPT offers recurring subscription plans billed in advance. By subscribing, you authorise us to charge your selected payment method on a recurring basis until cancelled.',
      },
      {
        number: 4,
        title: 'Free Trial Terms',
        content:
          'New eligible users may receive a 14-day free trial. At the end of the trial, your subscription will automatically begin and your payment method will be charged unless you cancel before the trial ends. One free trial per user.',
      },
      {
        number: 5,
        title: 'Cancellations & Refunds',
        content:
          'You can cancel your subscription at any time from your account settings. Cancellations take effect at the end of the current billing period. We do not offer refunds for partial billing periods or unused subscription time, except as required by law.',
      },
      {
        number: 6,
        title: 'Trainer Responsibilities',
        content:
          'You agree to use the Platform only for lawful purposes and in a professional manner. You are responsible for the accuracy of the information you provide and for obtaining any necessary consents from your clients.',
      },
      {
        number: 7,
        title: 'Content & Intellectual Property',
        content:
          'All content, features, and materials on miniPT are owned by miniPT or its licensors and are protected by copyright, trademark, and other intellectual property laws. You retain ownership of the content you create, while granting miniPT a limited license to host and display it as needed to provide the service.',
      },
      {
        number: 8,
        title: 'Limitation of Liability',
        content:
          'miniPT is provided "as is" and "as available." To the fullest extent permitted by law, miniPT shall not be liable for any indirect, incidental, or consequential damages arising out of your use of the Platform.',
      },
      {
        number: 9,
        title: 'Contact Information',
        content:
          'If you have any questions about these Terms, please contact us at legal@minipt.com or 12 Fitness Street, London, SW1A 1AA, United Kingdom.',
      },
    ],
  },
  checkin_policy: {
    title: 'Check-in policy',
    subtitle: 'Standards and schedules for client check-ins and video evaluations',
    lastUpdated: 'May 1, 2026',
    version: '1.4',
    clauses: [
      {
        number: 1,
        title: 'Scheduled Check-in Windows',
        content:
          'All check-ins must be booked at least 12 hours in advance within the trainer availability schedule. Video check-ins last 15 minutes unless an extended session is agreed upon.',
      },
      {
        number: 2,
        title: 'Client Preparation',
        content:
          'Clients should complete their weekly workout log and log any questions or injuries before the check-in call to ensure maximum coaching value.',
      },
      {
        number: 3,
        title: 'Rescheduling Guidelines',
        content:
          'Check-ins can be rescheduled up to 4 hours before the booked slot without penalty. Missed check-ins without notice will be forfeited.',
      },
      {
        number: 4,
        title: 'Video Connection & Technical Support',
        content:
          'Video check-ins are conducted via integrated secure video links. Trainers and clients must ensure stable internet access during their booked window.',
      },
    ],
  },
  cancellation_policy: {
    title: 'Cancellation and unused policy',
    subtitle: 'Policy regarding package cancellations, pauses, and unused coaching credits',
    lastUpdated: 'May 1, 2026',
    version: '1.2',
    clauses: [
      {
        number: 1,
        title: '4-Week Package Cycle',
        content:
          'Packages run for a fixed 4-week period starting on the designated program ready date. Services do not roll over past the 4-week cycle.',
      },
      {
        number: 2,
        title: 'Cancellation Notice',
        content:
          'Clients may request cancellation of ongoing coaching with 7 days notice prior to the end of their current 4-week cycle.',
      },
      {
        number: 3,
        title: 'Unused Inquiries and Check-ins',
        content:
          'Unused inquiries and check-ins expire at the conclusion of each 4-week coaching period and cannot be redeemed for cash or refunded.',
      },
      {
        number: 4,
        title: 'Trainer Cancellation & Replacement',
        content:
          'If a trainer is unable to deliver a scheduled program or check-ins due to unforeseen circumstances, an extension or prorated credit will be issued.',
      },
    ],
  },
};

export const validateReadyWithinTimeline = (readyWithin?: string): ReadyWithinValidationResult => {
  const trimmed = readyWithin?.trim();
  if (!trimmed) {
    return {
      isValid: false,
      error: 'Please select or enter when the initial program will be ready.',
    };
  }

  if (trimmed.includes('-')) {
    return {
      isValid: false,
      error: 'Initial program ready within cannot be negative.',
    };
  }

  const match = trimmed.match(/^(?:within\s+)?(\d+)\s*(days?|hours?|d|h)?$/i);
  if (!match) {
    return {
      isValid: false,
      error: 'Please enter a valid timeframe between 1 and 60 days (e.g. Within 3 days).',
    };
  }

  const num = parseInt(match[1], 10);
  const unit = match[2]?.toLowerCase();

  if (unit && (unit.startsWith('h') || unit === 'hours' || unit === 'hour')) {
    if (num <= 0) {
      return {
        isValid: false,
        error: 'Initial program ready within must be at least 1 hour.',
      };
    }
    if (num > 1440) {
      return {
        isValid: false,
        error: 'Initial program ready within cannot exceed 60 days (1,440 hours).',
      };
    }
    return {
      isValid: true,
      days: Math.max(1, Math.round(num / 24)),
    };
  }

  // Days
  if (num <= 0) {
    return {
      isValid: false,
      error: 'Initial program ready within must be at least 1 day.',
    };
  }
  if (num > 60) {
    return {
      isValid: false,
      error: 'Initial program ready within cannot exceed 60 days.',
    };
  }

  return {
    isValid: true,
    days: num,
  };
};

/**
 * Validates partial draft packages during background autosave
 */
export const validatePackageDraftClient = (
  pkg: Partial<TrainerPackage>,
): { isValid: boolean; error?: string } => {
  const hasContent = Boolean(
    (pkg.name && pkg.name.trim().length > 0) ||
    (pkg.shortDescription && pkg.shortDescription.trim().length > 0) ||
    (pkg.whoIsThisFor && pkg.whoIsThisFor.trim().length > 0) ||
    (pkg.price !== undefined && !isNaN(Number(pkg.price)) && Number(pkg.price) > 0),
  );

  if (!hasContent) {
    return { isValid: false, error: 'Draft has no content yet.' };
  }

  if (pkg.name && pkg.name.trim().length > 50) {
    return { isValid: false, error: 'Package Name cannot exceed 50 characters.' };
  }
  if (pkg.shortDescription && pkg.shortDescription.trim().length > 200) {
    return { isValid: false, error: 'Short Description cannot exceed 200 characters.' };
  }
  if (pkg.whoIsThisFor && pkg.whoIsThisFor.trim().length > 200) {
    return { isValid: false, error: 'Who this is for cannot exceed 200 characters.' };
  }
  if (pkg.price !== undefined && !isNaN(Number(pkg.price)) && Number(pkg.price) > 99999) {
    return { isValid: false, error: 'Price cannot exceed 99,999.' };
  }

  return { isValid: true };
};

/**
 * Strict Client-Side Validation (Identical parity with Backend API rules)
 */
export const validatePackageClient = (
  pkg: Partial<TrainerPackage>,
): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};
  const letterRegex = /[\p{L}]/u;

  // 1. Package Name
  const trimmedName = (pkg.name || '').trim();
  if (!trimmedName) {
    errors.name = 'Package Name is required.';
  } else if (trimmedName.length < 2) {
    errors.name = 'Package Name must be at least 2 characters.';
  } else if (trimmedName.length > 50) {
    errors.name = 'Package Name cannot exceed 50 characters.';
  } else if (!letterRegex.test(trimmedName)) {
    errors.name = 'Package Name must contain at least one alphabetical letter.';
  }

  // 2. Short Description
  const trimmedDesc = (pkg.shortDescription || '').trim();
  if (!trimmedDesc) {
    errors.shortDescription = 'Short Description is required.';
  } else if (trimmedDesc.length < 5) {
    errors.shortDescription = 'Short Description must be at least 5 characters.';
  } else if (trimmedDesc.length > 200) {
    errors.shortDescription = 'Short Description cannot exceed 200 characters.';
  } else if (!letterRegex.test(trimmedDesc)) {
    errors.shortDescription = 'Short Description must contain at least one alphabetical letter.';
  }

  // 3. Who is this for?
  const trimmedWhoFor = (pkg.whoIsThisFor || '').trim();
  if (!trimmedWhoFor) {
    errors.whoIsThisFor = 'Please specify who this package is for.';
  } else if (trimmedWhoFor.length < 5) {
    errors.whoIsThisFor = 'Who this is for must be at least 5 characters.';
  } else if (trimmedWhoFor.length > 200) {
    errors.whoIsThisFor = 'Who this is for cannot exceed 200 characters.';
  } else if (!letterRegex.test(trimmedWhoFor)) {
    errors.whoIsThisFor = 'Who this is for must contain at least one alphabetical letter.';
  }

  // 4. Price
  const rawPrice = pkg.price;
  const sym = (pkg.currency && CURRENCY_SYMBOLS[pkg.currency]) || '£';
  if (rawPrice === undefined || rawPrice === null || rawPrice === ('' as unknown as number)) {
    errors.price = 'Price is required.';
  } else {
    const priceVal = Number(rawPrice);
    if (isNaN(priceVal) || !Number.isFinite(priceVal) || priceVal <= 0) {
      errors.price = 'Please enter a valid price.';
    } else {
      const strVal = String(rawPrice).trim();
      const decPart = strVal.includes('.') ? strVal.split('.')[1] : '';
      if (decPart.length > 2) {
        errors.price = 'Price cannot have more than 2 decimal places.';
      } else if (priceVal < 1 || priceVal > 99999) {
        errors.price = `Price must be between ${sym}1 and ${sym}99,999.`;
      }
    }
  }

  // 5. Delivery SLA
  const readyResult = validateReadyWithinTimeline(pkg.delivery?.initialProgramReadyWithin);
  if (!readyResult.isValid && readyResult.error) {
    errors.initialProgramReadyWithin = readyResult.error;
  }

  // 6. Nutritional Plan Delivery SLA
  if (pkg.optionalExtras?.nutritionalPlan) {
    const nutDelivery = pkg.delivery?.nutritionalPlanDelivery;
    if (!nutDelivery || !VALID_NUTRITIONAL_DELIVERY_OPTIONS.includes(nutDelivery)) {
      errors.nutritionalPlanDelivery = 'Please select nutritional plan delivery timeline.';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const packagesService = {
  /**
   * Fetch all trainer packages via Cloud Functions with Firestore fallback
   */
  async getTrainerPackages(trainerId: string): Promise<TrainerPackage[]> {
    if (!trainerId) return [];

    try {
      const url = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_PACKAGES}?uid=${encodeURIComponent(trainerId)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (res.ok) {
        const json = await res.json();
        if (Array.isArray(json.data)) {
          const tierOrder: Record<string, number> = {
            'Package 1': 1,
            'Package 2': 2,
            'Package 3': 3,
          };
          return json.data.sort(
            (a: TrainerPackage, b: TrainerPackage) =>
              (tierOrder[a.presetTier] || 99) - (tierOrder[b.presetTier] || 99),
          );
        }
      }
    } catch (apiErr) {
      console.warn(
        '[packagesService.getTrainerPackages] API call failed, falling back to Firestore SDK:',
        apiErr,
      );
    }

    // Direct Firestore fallback
    try {
      const packagesRef = collection(db, 'trainer_app_users', trainerId, 'packages');
      const snapshot = await getDocs(packagesRef);

      const tierOrder: Record<string, number> = {
        'Package 1': 1,
        'Package 2': 2,
        'Package 3': 3,
      };

      if (!snapshot.empty) {
        const pkgs = snapshot.docs.map((docSnap) => ({
          ...docSnap.data(),
          id: docSnap.id,
        })) as TrainerPackage[];

        return pkgs.sort(
          (a, b) => (tierOrder[a.presetTier] || 99) - (tierOrder[b.presetTier] || 99),
        );
      }

      // Check parent doc summary
      const userRef = doc(db, 'trainer_app_users', trainerId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (Array.isArray(userData?.packages) && userData.packages.length > 0) {
          return (userData.packages as TrainerPackage[]).sort(
            (a, b) => (tierOrder[a.presetTier] || 99) - (tierOrder[b.presetTier] || 99),
          );
        }
      }

      return [];
    } catch (firestoreErr) {
      console.error('[packagesService.getTrainerPackages] Firestore SDK error:', firestoreErr);
      return [];
    }
  },

  /**
   * Fetch package templates from Master Data
   */
  async getPackageTemplates(): Promise<
    Array<Omit<TrainerPackage, 'trainerId' | 'createdAt' | 'updatedAt' | 'status'>>
  > {
    try {
      const url = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}trainer_get_package_templates`;
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        if (Array.isArray(json?.data) && json.data.length > 0) {
          return json.data;
        }
      }
    } catch (apiErr) {
      console.warn('[packagesService.getPackageTemplates] API failed, falling back:', apiErr);
    }

    try {
      const docRef = doc(db, 'trainer_app_master_data', 'package_templates');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        if (Array.isArray(data?.templates) && data.templates.length > 0) {
          return data.templates;
        }
      }
    } catch (fsErr) {
      console.warn('[packagesService.getPackageTemplates] Firestore read notice:', fsErr);
    }

    // Default static fallback
    return (['Package 1', 'Package 2', 'Package 3'] as PackagePresetTier[]).map((tier, idx) => ({
      ...DEFAULT_PACKAGE_PRESETS[tier],
      id: `pkg_template_${idx + 1}`,
      templateId: `pkg_template_${idx + 1}`,
    }));
  },

  /**
   * Fetch document / terms templates from Master Data
   */
  async getDocumentTemplates(): Promise<Record<TemplateType, DocumentTemplateConfig>> {
    try {
      const url = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_DOCUMENT_TEMPLATES}`;
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        if (json?.data && typeof json.data === 'object' && Object.keys(json.data).length > 0) {
          return json.data as Record<TemplateType, DocumentTemplateConfig>;
        }
      }
    } catch (apiErr) {
      console.warn('[packagesService.getDocumentTemplates] API failed, falling back:', apiErr);
    }

    try {
      const docRef = doc(db, 'trainer_app_master_data', 'document_templates');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        if (data && Object.keys(data).length > 0) {
          return data as Record<TemplateType, DocumentTemplateConfig>;
        }
      }
    } catch (fsErr) {
      console.warn('[packagesService.getDocumentTemplates] Firestore read notice:', fsErr);
    }

    return DEFAULT_DOCUMENT_TEMPLATES;
  },

  /**
   * Save (create or update) a trainer package via Cloud Functions with Firestore fallback
   */
  async saveTrainerPackage(
    trainerId: string,
    packageData: Partial<TrainerPackage>,
    signal?: AbortSignal,
  ): Promise<TrainerPackage> {
    if (!trainerId) {
      throw new Error(PACKAGE_ERROR_CODES.ERR_MISSING_UID);
    }

    // Client-side pre-validation
    const isDraft = (packageData.status ?? 'draft') === 'draft';
    if (isDraft) {
      const draftValidation = validatePackageDraftClient(packageData);
      if (!draftValidation.isValid) {
        throw new Error(draftValidation.error || 'Draft is incomplete');
      }
    } else {
      const clientValidation = validatePackageClient(packageData);
      if (!clientValidation.isValid) {
        const firstError = Object.values(clientValidation.errors)[0];
        throw new Error(firstError);
      }
    }

    // 1. Try Cloud Functions API
    try {
      const url = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_SAVE_PACKAGE}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: trainerId,
          package: packageData,
        }),
        signal,
      });

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        const errorCode = json?.code;
        const mappedMsg = errorCode ? PACKAGE_ERROR_CODES[errorCode] : null;
        throw new Error(
          mappedMsg || json?.message || json?.error || 'Failed to save package on server.',
        );
      }

      if (json?.data) {
        return json.data as TrainerPackage;
      }
    } catch (apiErr: unknown) {
      if (
        signal?.aborted ||
        (apiErr instanceof Error &&
          (apiErr.name === 'AbortError' || apiErr.message.includes('aborted')))
      ) {
        throw new DOMException('The user aborted a request.', 'AbortError');
      }
      // If server returned a business validation error (status 400), do NOT fall back
      const errMsg = apiErr instanceof Error ? apiErr.message : String(apiErr);
      if (Object.values(PACKAGE_ERROR_CODES).includes(errMsg)) {
        throw apiErr;
      }
      console.warn(
        '[packagesService.saveTrainerPackage] Cloud Function unavailable, using Firestore client:',
        apiErr,
      );
    }

    if (signal?.aborted) {
      throw new DOMException('The user aborted a request.', 'AbortError');
    }

    // 2. Direct Firestore fallback
    const packageId = packageData.id || `pkg_${Date.now()}`;
    const now = new Date().toISOString();

    const fullPackage: TrainerPackage = {
      id: packageId,
      trainerId,
      name: (packageData.name || '').trim(),
      presetTier: packageData.presetTier || DEFAULT_PACKAGE_TEMPLATE.tier,
      templateId:
        packageData.templateId || getPackageTemplateConfig(packageData.presetTier).templateId,
      shortDescription: (packageData.shortDescription || '').trim(),
      whoIsThisFor: (packageData.whoIsThisFor || '').trim(),
      price:
        packageData.price !== undefined && !isNaN(Number(packageData.price))
          ? Number(packageData.price)
          : 0,
      currency: packageData.currency || 'GBP',
      currencySymbol:
        packageData.currencySymbol || CURRENCY_SYMBOLS[packageData.currency || 'GBP'] || '£',
      duration: packageData.duration || '4 weeks/ 1 month',
      coaching: {
        inquiriesPerWeek: packageData.coaching?.inquiriesPerWeek ?? 1,
        checkInsPerWeek: packageData.coaching?.checkInsPerWeek ?? 2,
      },
      optionalExtras: {
        nutritionalPlan: Boolean(packageData.optionalExtras?.nutritionalPlan),
        nutritionalGuidance: Boolean(packageData.optionalExtras?.nutritionalGuidance),
      },
      delivery: {
        initialProgramReadyWithin:
          packageData.delivery?.initialProgramReadyWithin || 'Within 3 days',
        nutritionalPlanDelivery: packageData.delivery?.nutritionalPlanDelivery || 'Within 3 days',
      },
      terms: {
        coachingPeriod: packageData.terms?.coachingPeriod || '4-week coaching period',
        renewal: packageData.terms?.renewal || 'No automatic renewal',
      },
      status: packageData.status || 'published',
      createdAt: packageData.createdAt || now,
      updatedAt: now,
    };

    const docRef = doc(db, 'trainer_app_users', trainerId, 'packages', packageId);
    await setDoc(docRef, fullPackage, { merge: true });

    try {
      const existing = await this.getTrainerPackages(trainerId);
      const updatedList = existing.some((p) => p.id === packageId)
        ? existing.map((p) => (p.id === packageId ? fullPackage : p))
        : [...existing, fullPackage];

      const userRef = doc(db, 'trainer_app_users', trainerId);
      await updateDoc(userRef, { packages: updatedList });
    } catch (syncErr) {
      console.warn('[packagesService.saveTrainerPackage] Parent sync notice:', syncErr);
    }

    return fullPackage;
  },

  /**
   * Unpublish package via Cloud Functions with Firestore fallback
   */
  async unpublishTrainerPackage(trainerId: string, packageId: string): Promise<void> {
    if (!trainerId || !packageId) return;

    try {
      const url = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_UNPUBLISH_PACKAGE}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: trainerId,
          packageId,
        }),
      });

      if (res.ok) {
        return;
      }
    } catch (apiErr) {
      console.warn(
        '[packagesService.unpublishTrainerPackage] API call failed, using Firestore fallback:',
        apiErr,
      );
    }

    // Direct Firestore fallback
    const docRef = doc(db, 'trainer_app_users', trainerId, 'packages', packageId);
    await setDoc(docRef, { status: 'draft', updatedAt: new Date().toISOString() }, { merge: true });

    try {
      const existing = await this.getTrainerPackages(trainerId);
      const updatedList = existing.map((p) =>
        p.id === packageId ? { ...p, status: 'draft' as const } : p,
      );
      const userRef = doc(db, 'trainer_app_users', trainerId);
      await updateDoc(userRef, { packages: updatedList });
    } catch (err) {
      console.warn('[packagesService.unpublishTrainerPackage] Parent sync notice:', err);
    }
  },

  /**
   * Delete package via Cloud Functions with Firestore fallback
   */
  async deleteTrainerPackage(trainerId: string, packageId: string): Promise<void> {
    if (!trainerId || !packageId) return;

    try {
      const url = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_DELETE_PACKAGE}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: trainerId,
          packageId,
        }),
      });

      if (res.ok) {
        return;
      }
    } catch (apiErr) {
      console.warn(
        '[packagesService.deleteTrainerPackage] API call failed, using Firestore fallback:',
        apiErr,
      );
    }

    // Direct Firestore fallback
    const docRef = doc(db, 'trainer_app_users', trainerId, 'packages', packageId);
    await deleteDoc(docRef);

    try {
      const existing = await this.getTrainerPackages(trainerId);
      const updatedList = existing.filter((p) => p.id !== packageId);
      const userRef = doc(db, 'trainer_app_users', trainerId);
      await updateDoc(userRef, { packages: updatedList });
    } catch (err) {
      console.warn('[packagesService.deleteTrainerPackage] Parent sync notice:', err);
    }
  },
};
